import * as THREE from '@phdsoft/three';
import { ElementResultInfo } from 'projects/phdsoft/common/src/lib/models/element-result-info.model';
import type { InspectionPointInstanceManager } from '../digital-twin/graphics/inspection-point-instance-manager.service';
import type { PointShapeType } from '../digital-twin/graphics/point-shape-geometry-cache';
import { Collection } from '../platform/busines-data/Campaigns/Collection';
import { Section } from '../platform/busines-data/Framework/Section';
import { GaugingElement } from '../platform/busines-data/SectionElements/InspectionElements/gaugingElement';
import { StructuralElement } from '../platform/busines-data/SectionElements/StructureElements/StructuralElement';
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement';

/*
 * Snapshot/rollback half of the retarget transaction (AssetManagerInspectionRetarget.execute):
 * the snapshot records everything applyLocally() changes, rollbackRetarget() reverses it after a
 * failed save (including a Region/Section that addSurfaceElement created for a cross-region move).
 *
 * Instanced POINT inspections (dev GPU instancing): SurfaceDesign is a PhantomSurfaceDesign (no
 * Object3D, no geometry) and an InstancedMesh slot of the STRUCTURE section's batch draws the point.
 * The element is listed in a campaign-internal section but `inspection.section` is the structure
 * section (dev bulk-load rule). `retargetInstancedPoint` / the phantom branch of `rollbackRetarget`
 * move the listing, the phantom pose and the instance slot; real meshes keep the original code.
 */

/** Instance slot of an instanced POINT before the retarget. */
export interface RetargetInstanceSnapshot {
  /** Structure section whose batch holds the slot. */
  section: Section;
  shapeType: PointShapeType;
  /** Slot matrix in the batch's local space (section.group), the saved pose when hidden. */
  matrix: THREE.Matrix4;
  color: THREE.Color;
  visible: boolean;
}

/** Everything execute() changes locally, so a failed save can be reversed. */
export interface RetargetSnapshot {
  elementResult: ElementResultInfo;
  elementInfo: any;
  /** Section the element is detached from on a move: its own for a real mesh, the campaign one for a phantom. */
  section: Section | null;
  /** Campaign-internal section the element is listed in. */
  campaignSection: Section | null;
  /** `inspection.section` as it was (the structure section for a phantom). */
  structureSection: Section | null;
  campaign: Collection | null;
  position: THREE.Vector3; rotation: THREE.Euler; scale: THREE.Vector3;
  /** Null for a phantom (it has none). */
  geometry: THREE.BufferGeometry | null;
  /** SurfaceDesign is a PhantomSurfaceDesign (instanced POINT). */
  instanced: boolean;
  /** Phantom only: its slot, null when the manager does not know the element. */
  instance: RetargetInstanceSnapshot | null;
  /** Phantom only: `userData.SectionID`. */
  phantomSectionId: string | undefined;
  targetElement: WorkableElement | null; targetElements: WorkableElement[];
  regionId: string; regionIds: string[]; regionName: string;
  SectionId: string; SectionName: string;
  targetLabels: string; isOrphan: boolean; scope: string; label: any; visible: boolean;
  oldListIndex: number; addedToNewList: boolean; createdSection: boolean;
  /** The inspection campaign had no Region for the target's region: addSurfaceElement creates it. */
  createdRegion: boolean;
}

/** What `retargetInstancedPoint` needs besides the inspection, the target and the snapshot. */
export interface InstancedPointMove {
  /** Built preview point: only its pose is taken (the caller releases its geometry). */
  pointMesh: THREE.Mesh | null;
  shapeType: PointShapeType;
  /** Section or region changes: the campaign listing moves. */
  moves: boolean;
  campaign: Collection | null;
  manager: InspectionPointInstanceManager | null;
}

/** Colour of a slot the manager cannot read (dev's grey insertion colour). */
const FALLBACK_INSTANCE_COLOR = 0xe5e5e5;

/** Snapshot of `inspection` before it is retargeted to `target`. */
export function takeRetargetSnapshot(inspection: GaugingElement, target: WorkableElement,
  manager?: InspectionPointInstanceManager | null): RetargetSnapshot {
  const mesh = inspection.SurfaceDesign;
  const userData = mesh.userData; // read before the narrowing below (a phantom is no Object3D)
  const instanced = !(mesh instanceof THREE.Object3D);
  const oldTarget = inspection.targetElement;
  const campaign = inspection.Campaign ?? null;
  const region = campaign?.RegionById(target.regionId);
  const createdRegion = !region;
  const campaignSection = findSection(campaign, inspection.regionId, inspection.SectionId);
  const structureSection = inspection.section ?? null;
  return {
    // buildRetargetedResult never mutates the current result, so the original object is kept and
    // restored as is (a JSON clone would turn CheckList dates into strings); ElementInfo is deep-copied.
    elementResult: inspection.elementResult,
    elementInfo: JSON.parse(JSON.stringify(inspection.elementResult.ElementInfo ?? null)),
    // a real mesh is listed where inspection.section points; a phantom's section is the structure one
    section: instanced ? campaignSection : (structureSection ?? campaignSection),
    campaignSection, structureSection,
    campaign,
    position: mesh.position.clone(), rotation: mesh.rotation.clone(), scale: mesh.scale.clone(),
    geometry: instanced ? null : mesh.geometry as THREE.BufferGeometry,
    instanced,
    instance: instanced ? instanceSnapshot(manager, inspection, structureSection) : null,
    phantomSectionId: instanced ? userData?.SectionID : undefined,
    targetElement: oldTarget, targetElements: [...(inspection.targetElements ?? [])],
    regionId: inspection.regionId, regionIds: inspection.regionIds, regionName: inspection.regionName,
    SectionId: inspection.SectionId, SectionName: inspection.SectionName,
    targetLabels: inspection.targetLabels, isOrphan: inspection.isOrphan, scope: inspection.scope,
    label: inspection.label, visible: mesh.visible,
    oldListIndex: oldTarget instanceof StructuralElement ? oldTarget.gaugingElements.indexOf(inspection) : -1,
    addedToNewList: false,
    createdSection: createdRegion || !region.sections.some(s => s.Guid === target.SectionId),
    createdRegion,
  };
}

/**
 * applyLocally() for a phantom: takes the built point's pose (never its geometry), moves the
 * campaign listing on a section/region change (the structure section is restored on the element,
 * dev bulk-path pattern), retargets, and re-creates the instance slot in the target section's batch
 * of `m.shapeType` at the new pose. Visibility is not written: instances follow section.group.
 */
export function retargetInstancedPoint(inspection: GaugingElement, target: WorkableElement,
  m: InstancedPointMove, s: RetargetSnapshot): void {
  const phantom = inspection.SurfaceDesign;
  if (m.pointMesh) {
    phantom.position.copy(m.pointMesh.position);
    phantom.rotation.copy(m.pointMesh.rotation); // the phantom syncs its quaternion from the Euler
    phantom.quaternion.copy(m.pointMesh.quaternion);
    phantom.scale.copy(m.pointMesh.scale);
  }
  if (m.moves) s.campaignSection?.detachElementByIdOnly(inspection.Guid);
  inspection.retarget(target);
  if (m.moves) m.campaign?.addSurfaceElement(inspection); // sets the campaign section: restored below
  inspection.section = target.section ?? s.structureSection;
  if (phantom.userData && inspection.section) phantom.userData.SectionID = inspection.section.Guid;
  if (!m.manager || !inspection.section) return;
  const color = new THREE.Color(FALLBACK_INSTANCE_COLOR);
  m.manager.getInstanceColor(inspection, color); // the live colour (the tool's pick highlight)
  const matrix = new THREE.Matrix4().compose(phantom.position, phantom.quaternion, phantom.scale);
  reinstancePoint(m.manager, inspection, inspection.section, m.shapeType, matrix, color);
}

/** Reverses applyLocally() after a failed save; never throws. */
export function rollbackRetarget(inspection: GaugingElement, target: WorkableElement, s: RetargetSnapshot,
  manager?: InspectionPointInstanceManager | null): void {
  try {
    const mesh = inspection.SurfaceDesign;
    if (s.instanced) {
      rollbackInstancedPoint(inspection, target, s, manager ?? null);
    } else if (inspection.section !== s.section) {
      const newSection = inspection.section;
      newSection?.detachElementByIdOnly(inspection.Guid);
      dropCreatedSection(newSection, target, s);
      // restore the pose first: addSurfaceElement expands the section box from the current pose
      restoreMeshPose(mesh, s);
      s.section?.addSurfaceElement(inspection);
    }
    (inspection as any)._targetElement = s.targetElement; // private field, no setter (orphans: null)
    inspection.targetElements = s.targetElements;
    inspection.regionId = s.regionId;
    inspection.regionIds = s.regionIds;
    inspection.regionName = s.regionName;
    inspection.SectionId = s.SectionId;
    inspection.SectionName = s.SectionName;
    inspection.targetLabels = s.targetLabels;
    inspection.isOrphan = s.isOrphan;
    inspection.scope = s.scope;
    inspection.label = s.label;
    if (s.addedToNewList && target instanceof StructuralElement) {
      const i = target.gaugingElements.indexOf(inspection);
      if (i >= 0) target.gaugingElements.splice(i, 1);
    }
    const oldList = s.oldListIndex >= 0 ? (s.targetElement as StructuralElement).gaugingElements : null;
    if (oldList && !oldList.includes(inspection)) oldList.splice(s.oldListIndex, 0, inspection);
    restoreMeshPose(mesh, s); // no-op when already restored above
    if (!s.instanced) mesh.visible = s.visible; // a phantom's visibility was never written
    s.elementResult.ElementInfo = s.elementInfo;
    inspection.elementResult = s.elementResult;
  } catch (error) {
    console.error('[InspectionRetarget] rollback failed', error);
  }
}

/**
 * Phantom half of the rollback: campaign listing back in `campaignSection` (a section/region the
 * move created is dropped again), structure section and pose restored, slot re-created from the
 * snapshot (batch, shape, matrix, colour) with the phantom's hidden state.
 */
function rollbackInstancedPoint(inspection: GaugingElement, target: WorkableElement, s: RetargetSnapshot,
  manager: InspectionPointInstanceManager | null): void {
  const sections = (s.campaign?.regionsWorkableElements ?? []).flatMap(r => r?.sections ?? []);
  for (const listed of sections.filter(sec => sec !== s.campaignSection && sec.surfaceElements.includes(inspection))) {
    listed.detachElementByIdOnly(inspection.Guid);
    dropCreatedSection(listed, target, s);
  }
  if (s.campaignSection && !s.campaignSection.surfaceElements.includes(inspection)) s.campaignSection.addSurfaceElement(inspection);
  inspection.section = s.structureSection;
  const phantom = inspection.SurfaceDesign;
  restoreMeshPose(phantom, s);
  if (phantom.userData) phantom.userData.SectionID = s.phantomSectionId;
  if (!manager) return;
  if (s.instance) {
    reinstancePoint(manager, inspection, s.instance.section, s.instance.shapeType, s.instance.matrix, s.instance.color);
  } else {
    manager.removeInstance(inspection);
  }
}

/**
 * Moves the element's slot: remove, add to `section`'s batch of `shapeType`, parent a new batch to
 * section.group (as dev's _addInstanceForPointElement), hide it again when the phantom is hidden
 * (removeInstance forgets the visibility state), and re-apply the phantom's named material colour.
 * Buffers and the bounding sphere are refreshed by the manager's flushDirtyBuffers on the next frame.
 * Also used by the recompartment scene patch (recompartment/recompartment-point-instances.ts).
 */
export function reinstancePoint(manager: InspectionPointInstanceManager, inspection: GaugingElement, section: Section,
  shapeType: PointShapeType, matrix: THREE.Matrix4, color: THREE.Color): void {
  manager.removeInstance(inspection);
  manager.addInstance(section, shapeType, inspection, matrix.clone(), color);
  attachSectionBatches(manager, section);
  const phantom = inspection.SurfaceDesign;
  if (phantom.visible === false) manager.setInstanceVisible(inspection, false);
  phantom.material = phantom.material; // setter: named colour on the new slot (null keeps `color`)
}

/** Parents the section's batches that are not in the scene yet to its group. */
function attachSectionBatches(manager: InspectionPointInstanceManager, section: Section): void {
  for (const batch of manager.getMeshesForSection(section)?.values() ?? []) {
    if (!batch.parent) section.group.add(batch);
  }
}

/** Slot of a registered phantom in its batch's local space; null when not registered (also used by the recompartment scene patch). */
export function instanceSnapshot(manager: InspectionPointInstanceManager | null | undefined, inspection: GaugingElement,
  structureSection: Section | null): RetargetInstanceSnapshot | null {
  const info = manager?.getInstanceInfo(inspection);
  if (!info || !structureSection) return null; // dev keys every batch by the element's structure section
  const visible = manager.getInstanceVisible(inspection);
  const matrix = new THREE.Matrix4();
  if (visible) {
    info.mesh.getMatrixAt(info.instanceIndex, matrix);
  } else {
    // the slot holds the zero-scale matrix: the saved pose in world space, back to the batch's space
    manager.getInstanceWorldMatrix(inspection, matrix); // also refreshes info.mesh.matrixWorld
    matrix.premultiply(info.mesh.matrixWorld.clone().invert());
  }
  const color = new THREE.Color(FALLBACK_INSTANCE_COLOR);
  manager.getInstanceColor(inspection, color);
  return { section: structureSection, shapeType: info.shapeType, matrix, color, visible };
}

/** Removes a section (and region) that the move created in the campaign once it is empty again. */
function dropCreatedSection(newSection: Section | null, target: WorkableElement, s: RetargetSnapshot): void {
  if (!s.createdSection || !newSection || newSection.surfaceElements.length || newSection.linearElements.length) return;
  const region = s.campaign?.RegionById(target.regionId);
  const index = region?.sections.indexOf(newSection) ?? -1;
  if (index < 0) return;
  region.sections.splice(index, 1);
  newSection.group.parent?.remove(newSection.group);
  if (s.createdRegion && region.sections.length === 0) {
    s.campaign.regionsWorkableElements.splice(s.campaign.regionsWorkableElements.indexOf(region), 1);
    s.campaign.group?.remove(region.group);
  }
}

/** The replaced geometry is disposed by execute() once rollback is done; a phantom has none. */
function restoreMeshPose(mesh: THREE.Mesh, s: RetargetSnapshot): void {
  if (!s.instanced) mesh.geometry = s.geometry;
  mesh.position.copy(s.position);
  mesh.rotation.copy(s.rotation);
  mesh.scale.copy(s.scale);
}

function findSection(campaign: Collection | null, regionId: string, sectionId: string): Section | null {
  return campaign?.RegionById(regionId)?.sections.find(s => s.Guid === sectionId) ?? null;
}
